import { ColumnDef, sortingFns } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { GTColumnSelectAnchor, GTExpander, GTFrameworkCell, GTModifierLabel } from 'src/components/GenericTable';
import { formatDate, formatNumber } from 'src/utils/misc';
import { filter } from 'lodash';
import { Link } from 'react-router-dom';
import { GTInternalIds } from 'src/components/GenericTable/types';
import _ from 'lodash';
import { ControlStateItem } from 'src/components/ProjectedRisk/types';
import Input from 'src/components/form/Input';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import useVersion from 'src/hooks/useVersion';
import { encodeQueryParams } from 'src/api/client';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { TableIds } from 'src/api/types/misc';
import { OpenInNew } from '@mui/icons-material';
import { TextOverflowTooltip } from 'src/common';
import { getMaturityLevelName, maturityLevelNames } from 'src/api/types';

type UseColumns = (a: {
  data?: ControlStateItem[];
  scenarioId: string;
  assessmentId: string;
  toggleControl: (isEnabled: boolean, id: string) => Promise<void>;
}) => {
  columns: ColumnDef<ControlStateItem>[];
  GTColumnSelectAnchorExported: JSX.Element;
};

const defaultColumns = [
  'strategy',
  'name',
  'owner',
  'status',
  'expected_active_date',
  '_mod_applied',
  '_projected_status',
];

export const useColumnsControls: UseColumns = ({ data = [], scenarioId, assessmentId, toggleControl }) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(
    `${TableIds.prScenarioScenarios}-columns`,
    defaultColumns,
  );
  const { versionId } = useVersion();

  const columns = useMemo(() => {
    const internalIds = [GTInternalIds.expander, GTInternalIds.tdm, GTInternalIds.checker];

    const list: ColumnDef<ControlStateItem>[] = [
      {
        id: GTInternalIds.expander,
        header: () => null,
        cell: ({ row }) => {
          return row.getCanExpand() ? (
            <GTExpander onClick={row.getToggleExpandedHandler()} isExpanded={row.getIsExpanded()} />
          ) : null;
        },
        size: 1,
        maxSize: 20,
        enableSorting: false,
      },
      {
        id: 'ux_id',
        header: 'Id',
        accessorFn: (row) => row.control.id || null,
        cell: (info) => info.getValue(),
      },
      {
        id: 'strategy',
        header: 'Strategy',
        accessorFn: (row) => row.control.strategy || null,
        cell: (info) => info.getValue(),
      },
      {
        id: 'name',
        header: 'Name',
        accessorFn: (row) => row.control.name,
        cell: (info) => (
          <TextOverflowTooltip>
            <Link
              className="table-link"
              to={`/risk-management/control/${info.row.original.control.id}${encodeQueryParams({
                version: versionId,
                assessmentId,
              })}`}
            >
              {info.getValue() as string}
            </Link>
          </TextOverflowTooltip>
        ),
        sortingFn: sortingFns.text,
      },
      {
        id: 'owner',
        header: 'Owner',
        accessorFn: (row) => row.control.owner?.text || null,
        cell: (info) => info.getValue(),
        sortingFn: sortingFns.text,
      },
      {
        id: 'framework_controls',
        header: 'Framework',
        cell: (info) => <GTFrameworkCell controls={info.row.original.control.frameworkControls} />,
        sortingFn: sortingFns.text,
      },
      {
        id: 'annual_cost',
        header: 'Annual Cost',
        accessorFn: (row) => row.control.annualCost || null,
        cell: ({ row }) => formatNumber(row.original.control.annualCost, { symbol: true }),
        sortingFn: sortingFns.alphanumeric,
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (row) => row.control.status || null,
        cell: (info) => info.getValue(),
      },
      {
        id: 'existingMaturity',
        header: 'Existing Maturity',
        cell: ({ row }) => getMaturityLevelName(row.original.control.existingMaturity),
      },
      {
        id: 'targetMaturity',
        header: 'Target Maturity',
        cell: ({ row }) => getMaturityLevelName(row.original.control.targetMaturity),
      },
      {
        id: 'updated_at',
        header: 'Last Edited',
        accessorFn: (row) => row.control.meta?.updatedAt || null,
        cell: ({ row }) => formatDate(row.original.control.meta?.updatedAt),
      },
      {
        id: 'expected_active_date',
        header: 'Expected Live Date',
        accessorFn: (row) => row.control.expectedActiveDate || null,
        cell: ({ row }) => formatDate(row.original.control.expectedActiveDate),
      },
      {
        id: '_mod_applied',
        header: 'Modifiers',
        accessorFn: (row) => (row.control.modifiers?.treat.length || 0) + (row.control.modifiers?.transfer.length || 0),
        cell: (info) => {
          const satisfiesModifier = true;
          const modifiersAvailable =
            info.row.original.control.strategy === 'Treat' || info.row.original.control.strategy === 'Transfer';
          if (!modifiersAvailable) return null;
          return <GTModifierLabel length={info.getValue() as number} satisfiesModifier={satisfiesModifier} />;
        },
      },
      {
        id: '_projected_status',
        header: 'Projected Status',
        accessorFn: (row) => row.control.expectedActiveDate || null,
        cell: ({ row }) => {
          if (row.original.control.strategy !== 'Treat' && row.original.control.strategy !== 'Transfer') {
            return null;
          }
          return (
            <Input
              onClick={(e) => e.stopPropagation()}
              checked={row.original.isEnabled}
              onChange={(e) => {
                toggleControl(e.target.checked, row.original.control.id);
                mpEvent(MPEvents.ButtonClick, {
                  button: '[projected status toggle]',
                  modal: 'Projected risk:Scenario modal',
                  tags: ['SCENARIO', 'MODIFIERS'],
                });
              }}
              className="switch"
              type="checkbox"
              disabled={false}
            />
          );
        },
      },
      {
        id: 'source',
        header: 'Link',
        accessorFn: (row) => row.control.linkUrl || null,
        cell: (info) => {
          const url = info.getValue();
          const linkText = (info.row.original.control.linkText || url) as string;

          return url ? (
            <a
              href={url as string}
              target="_blank"
              rel="noopener noreferrer"
              className="table-link"
              onClick={(e) => e.stopPropagation()}
            >
              {linkText}{' '}
              <OpenInNew
                css={`
                  font-size: 14px !important;
                  color: inherit;
                `}
              />
            </a>
          ) : (
            <span>{linkText}</span>
          );
        },
      },
      {
        id: GTInternalIds.tdm,
        header: () => null,
        cell: () => null,
        size: 1,
      },
    ];

    return filter(list, (el) => [...internalIds, ...selectedColumns].includes(el.id as string));
  }, [data, selectedColumns, scenarioId, versionId, assessmentId]);

  const GTColumnSelectAnchorExported = (
    <GTColumnSelectAnchor
      tableId={TableIds.prScenarioScenarios}
      options={[
        { key: 'ux_id', title: 'Id' },
        { key: 'strategy', title: 'Strategy' },
        { key: 'name', title: 'Name' },
        { key: 'owner', title: 'Owner' },
        { key: 'framework_controls', title: 'Framework' },
        { key: 'annual_cost', title: 'Annual Cost' },
        { key: 'status', title: 'Status' },
        { key: 'existingMaturity', title: 'Existing Maturity' },
        { key: 'targetMaturity', title: 'Target Maturity' },
        { key: 'updated_at', title: 'Last Edited' },
        { key: 'expected_active_date', title: 'Expected Live Date' },
        { key: '_mod_applied', title: 'Modifiers' },
        { key: '_projected_status', title: 'Projected Status' },
        { key: 'source', title: 'Link' },
      ]}
      defaultOptions={defaultColumns}
      value={selectedColumns}
      onChange={setSelectedColumns}
    />
  );

  return {
    columns,
    GTColumnSelectAnchorExported,
  };
};
